import type { Lang } from "@/lib/i18n";

/** Map a BCP 47 language tag to en or ar when recognized. */
export function langFromTag(tag: string): Lang | null {
  const base = tag.trim().toLowerCase().split("-")[0];
  if (base === "ar") return "ar";
  if (base === "en") return "en";
  return null;
}

/**
 * Pick en/ar from an Accept-Language header (first supported match wins).
 */
export function pickLangFromAcceptLanguage(
  header: string | null | undefined,
  fallback: Lang,
): Lang {
  if (!header?.trim()) return fallback;

  for (const part of header.split(",")) {
    const tag = part.split(";")[0]?.trim();
    if (!tag) continue;
    const lang = langFromTag(tag);
    if (lang) return lang;
  }
  return fallback;
}

/** Read the user's browser language list (client only). */
export function detectBrowserLang(fallback: Lang): Lang {
  if (typeof navigator === "undefined") return fallback;

  const tags = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const tag of tags) {
    if (!tag) continue;
    const lang = langFromTag(tag);
    if (lang) return lang;
  }
  return fallback;
}

/**
 * Resolve the active language from site settings and optional Accept-Language.
 * Used on the server for the first HTML paint.
 */
export function resolveLangForSite(
  mode: string,
  siteDefault: Lang,
  acceptLanguage?: string | null,
): Lang {
  if (mode === "ar") return "ar";
  if (mode === "en") return "en";
  return pickLangFromAcceptLanguage(acceptLanguage, siteDefault);
}

/**
 * Resolve language in bilingual mode: saved preference, then browser, then site default.
 */
export function resolveBilingualLang(
  siteDefault: Lang,
  stored: string | null | undefined,
  browserLang?: Lang,
): Lang {
  if (stored === "en" || stored === "ar") return stored;
  if (browserLang) return browserLang;
  return siteDefault;
}
